import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  TableConfiguration,
  TableModule,
} from '@lifeomic/chroma-react/components/TableModule';
import Papa from 'papaparse';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

/**
 * Maximum number of data rows we'll attempt to render in a single CSV viewer.
 * Beyond this, the file is almost certainly intended for spreadsheet tools and
 * trying to render it inline tends to make the browser unhappy.
 */
export const CSV_MAX_RENDERED_ROWS = 1000;

const messages = defineMessages({
  parseError: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentTable.parseError',
    defaultMessage: 'Could not parse this CSV file.',
  },
  emptyCsv: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentTable.emptyCsv',
    defaultMessage: 'This CSV file is empty.',
  },
  truncated: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentTable.truncated',
    defaultMessage:
      'Showing the first {shown, number} of {total, number} rows. Download the file to see the full contents.',
  },
  unnamedColumn: {
    id: 'phc-web-toolkit.subjectViewer.attachments.csvAttachmentTable.unnamedColumn',
    defaultMessage: 'Column {index, number}',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '100%',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
  },
  scroll: {
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'auto',
    width: '100%',
  },
  notice: {
    color: theme.palette.text.hint,
    padding: theme.spacing(0.5, 1),
  },
  error: {
    color: theme.palette.error.main,
    padding: theme.spacing(2),
  },
}));

export type ParsedCsv = {
  headers: string[];
  rows: string[][];
  /** Total number of data rows discovered during parsing (pre-truncation). */
  totalRows: number;
};

const normalizeHeaders = (
  rawHeaders: string[],
  unnamedLabel: (index: number) => string,
): string[] => {
  const seen = new Map<string, number>();
  return rawHeaders.map((h, i) => {
    const trimmed = (h ?? '').trim();
    const base = trimmed.length === 0 ? unnamedLabel(i + 1) : trimmed;
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return occurrence === 0 ? base : `${base} (${occurrence + 1})`;
  });
};

/**
 * Parse a CSV string into a header row and data rows, capped at
 * {@link CSV_MAX_RENDERED_ROWS} rows so a runaway file doesn't lock up the UI.
 *
 * Exported for unit testing — most callers should use {@link CsvAttachmentTable}.
 */
export const parseCsvForDisplay = (
  csvText: string,
  options: {
    maxRows?: number;
    unnamedLabel?: (index: number) => string;
  } = {},
): ParsedCsv => {
  const maxRows = options.maxRows ?? CSV_MAX_RENDERED_ROWS;
  const unnamedLabel = options.unnamedLabel ?? (i => `Column ${i}`);

  const result = Papa.parse(csvText ?? '', {
    skipEmptyLines: 'greedy',
    header: false,
  });

  const allRows = ((result.data as unknown[][]) ?? []).filter(
    (row): row is string[] =>
      Array.isArray(row) &&
      row.some(cell => (cell ?? '').toString().length > 0),
  );

  if (allRows.length === 0) {
    return { headers: [], rows: [], totalRows: 0 };
  }

  const [firstRow, ...rest] = allRows as string[][];
  const headers = normalizeHeaders(
    firstRow.map(c => (c ?? '').toString()),
    unnamedLabel,
  );

  const rows = rest
    .slice(0, maxRows)
    .map(row => headers.map((_, idx) => (row[idx] ?? '').toString()));

  return {
    headers,
    rows,
    totalRows: rest.length,
  };
};

export type CsvAttachmentTableProps = {
  csvText: string;
  /** Optional cap; defaults to {@link CSV_MAX_RENDERED_ROWS}. */
  maxRows?: number;
  /** Optional className applied to the wrapper. */
  className?: string;
  /** Optional test id for the scroll container. */
  'data-testid'?: string;
};

/**
 * Render a CSV string as a sortable table. Truncates very large files with a
 * notice so the in-browser preview stays responsive.
 */
export const CsvAttachmentTable: React.FC<CsvAttachmentTableProps> = ({
  csvText,
  maxRows = CSV_MAX_RENDERED_ROWS,
  className,
  'data-testid': testId,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const parsed = useMemo(() => {
    try {
      return parseCsvForDisplay(csvText, {
        maxRows,
        unnamedLabel: i => formatMessage(messages.unnamedColumn, { index: i }),
      });
    } catch {
      return null;
    }
  }, [csvText, maxRows, formatMessage]);

  const config = useMemo<TableConfiguration<string[]>[] | null>(() => {
    if (!parsed) return null;
    return parsed.headers.map((header, columnIndex) => ({
      header: { label: header },
      cell: {
        content: (row: string[]) => row[columnIndex] ?? '',
      },
    }));
  }, [parsed]);

  if (!parsed) {
    return (
      <Box className={classes.error} data-testid={testId}>
        <Text>{formatMessage(messages.parseError)}</Text>
      </Box>
    );
  }

  if (parsed.headers.length === 0) {
    return (
      <Box className={classes.notice} data-testid={testId}>
        <Text>{formatMessage(messages.emptyCsv)}</Text>
      </Box>
    );
  }

  const truncated = parsed.totalRows > parsed.rows.length;

  return (
    <Box className={`${classes.root} ${className ?? ''}`} data-testid={testId}>
      <Box className={classes.scroll}>
        <TableModule
          config={config ?? []}
          data={parsed.rows}
          maxCellWidth={3}
        />
      </Box>
      {truncated && (
        <Text className={classes.notice} size="caption">
          {formatMessage(messages.truncated, {
            shown: parsed.rows.length,
            total: parsed.totalRows,
          })}
        </Text>
      )}
    </Box>
  );
};
